import _template from 'lodash/template';
import { CampaignState, CampaignType, CampaignTypeFilter, Day } from 'modules/domains/campaign';
import { MetaApprovalStatusDefinition, MetaQualityRating } from 'modules/graphql';
import {
  AbortedBadge,
  DraftBadge,
  InAppealBadge,
  RunningBadge,
  ScheduledBadge,
  UnknownBadge,
} from 'ui/badge';
import { BADGE_VARIANTS } from 'ui/badge/constants';
import { TimeIn24HourClock } from 'ui/datePicker';
import { QualityRatingHigh, QualityRatingLow, QualityRatingMedium } from 'ui/icons';
import { TopTooltip } from 'ui/tooltip';

import { Nullable } from 'utils/tsHelpers';
import { TimeDuration } from '../alerts/types';
import { CampaignStep } from '../editor/types';
import { RecurrenceScheduleType } from './types';

/**
 * These campaign type filters are scheduled (vs. triggered).
 */
export const SCHEDULED_CAMPAIGN_TYPE_FILTERS = [
  CampaignTypeFilter.Blast,
  CampaignTypeFilter.Eligibility,
];

/**
 * These campaign types are scheduled (vs. triggered).
 */
export const SCHEDULED_CAMPAIGN_TYPES = [CampaignType.Blast, CampaignType.Eligibility];

/**
 * These campaign types are considered triggered and does not have a predefined audience (aka lists)
 */
export const TRIGGERED_CAMPAIGN_TYPES = [CampaignType.ApiTriggered, CampaignType.WorkflowTriggered];

/**
 * These states are not available for triggered campaigns
 */
export const TRIGGERED_CAMPAIGN_UNSUPPORTED_STATES = [
  CampaignState.Scheduled,
  CampaignState.Aborted,
];

/**
 * These states allow editing campaign settings across campaign types
 */
export const CAMPAIGN_SETTINGS_EDITABLE_STATES = [
  CampaignState.Draft,
  CampaignState.Ready,
  CampaignState.Scheduled,
  CampaignState.Recurring,
];

/**
 * These states allow editing campaign contents(templates) across campaign types
 */
export const CAMPAIGN_TEMPLATE_EDITABLE_STATES = [
  ...CAMPAIGN_SETTINGS_EDITABLE_STATES,
  CampaignState.Running,
];

/**
 * The following campaign states do not support modifying Performance Alerts settings
 * Note: we do not support editing `Finished` `Blast` campaigns as well
 */
export const CAMPAIGN_ALERT_UPDATES_DISABLED_STATES = [
  CampaignState.Aborted,
  CampaignState.Archived,
  CampaignState.Recalled,
];

/**
 * These campaign steps are not require before a campaign can be scheduled or activated
 */
export const OPTIONAL_CAMPAIGN_STEPS = [
  CampaignStep.Display,
  CampaignStep.Delivery,
  CampaignStep.ConversionAndExperiment,
  CampaignStep.Conversions,
];

/** Quiet hours */
export const DEFAULT_QUIET_HOURS_START: TimeIn24HourClock = '20:00';
export const DEFAULT_QUIET_HOURS_END: TimeIn24HourClock = '09:00';
export const QUIET_HOURS_SMS_DISCLAIMER =
  'This can help with, but does not guarantee, compliance for SMS campaigns.';

/** Send Time Optimization */
export const DEFAULT_STO_COMPLETION_TIME = 24;
export const MIN_STO_COMPLETION_TIME = 6;
export const MAX_STO_COMPLETION_TIME = 24;

/** Campaign Rate Limiting */
export const DEFAULT_RATE_LIMIT_PER_MINUTE = 1000;
export const MIN_RATE_LIMIT_PER_MINUTE = 120; // @see https://github.com/Iterable/iterable/blob/020cf6a7ef076d89fd1b0d26d544e6553eb4f808/conf/iterable-common.conf#L594
export const MIN_RATE_LIMIT_PER_SECOND = MIN_RATE_LIMIT_PER_MINUTE / 60; // minutes / 60 = seconds

/** Conversions */
export const DEFAULT_CONVERSIONS_PERIOD_HOUR = 24;
export const MIN_CONVERSIONS_PERIOD_HOUR = 1;
export const MAX_CONVERSIONS_PERIOD_HOUR = 168;

/**
 * The maximum days a campaign can be scheduled ahead
 */
export const MAX_CAMPAIGN_SCHEDULE_AHEAD_DAYS = 21;

/**
 * The minimum hours from now the default schedule time should be.
 */
export const MIN_SCHEDULE_TIME_HOURS_IN_THE_FUTURE = 1;

/**
 * The maximum days before a recurring campaign re-run
 */
export const MAX_CAMPAIGN_RECURRENCE_FREQUENCY = 100;

/**
 * The minimum days before a recurring campaign re-run
 */
export const MIN_CAMPAIGN_RECURRENCE_FREQUENCY = 1;

/**
 * The default days before a recurring campaign re-run
 */
export const DEFAULT_CAMPAIGN_RECURRENCE_FREQUENCY = 1;

/**
 * Default hours ahead recurring campaigns should be created
 */
export const DEFAULT_PRE_CREATE_RECURRING_CAMPAIGN_HOURS = 1;

/**
 * Maximum hours a recurring campaign can be created ahead
 */
export const MAX_PRE_CREATE_RECURRING_CAMPAIGN_HOURS = 24;

/**
 * Minimum hours a recurring campaign can be created ahead
 */
export const MIN_PRE_CREATE_RECURRING_CAMPAIGN_HOURS = 0;

/**
 * Minimum days a recurring campaign can end on from today
 */
export const MIN_RECURRING_CAMPAIGN_END_ON_DAYS = 1;

/**
 * Supported scheduling time formats
 * @see https://momentjs.com/docs/#/parsing/string-formats/
 */
export const SUPPORTED_CAMPAIGN_SCHEDULING_MOMENT_TIME_FORMATS = [
  'YYYY-MM-DD hh:mm A',
  'YYYY-MM-DD h:mm A',
  'YYYY-MM-DD hh:mm a',
  'YYYY-MM-DD h:mm a',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD H:mm',
];

/** Campaign error messages */
export const CAMPAIGN_ERROR_MESSAGES = {
  sameQuietHoursStartAndEndTime: 'Quiet hours can’t start and end at the same time.',
  stoCompletionTimeRange: `Completion time needs to be between ${MIN_STO_COMPLETION_TIME} and ${MAX_STO_COMPLETION_TIME} hours.`,
  minimumRateLimitValuePerMinute: `Must be at least ${MIN_RATE_LIMIT_PER_MINUTE}.`,
  minimumRateLimitValuePerSecond: `Must be at least ${MIN_RATE_LIMIT_PER_SECOND}.`,
  invalidAttributionPeriodRange: `Please use a time period greater than ${MIN_CONVERSIONS_PERIOD_HOUR} hour and less than ${
    MAX_CONVERSIONS_PERIOD_HOUR + 1
  } hours.`,
  quietHoursAndStoOverlap:
    'Your quiet hours window overlaps with your send time optimization window—some or all of your sends won’t be optimized.',
};

/**
 * Campaigns alerts warning messages
 */
const ALERTS_CONFLICT_MESSAGE_ENDING = 'may conflict with alert parameters and cause false alerts.';
export const ALERTS_STO_QH_CONFLICT_MESSAGE = `Quiet hours and Send Time Optimization ${ALERTS_CONFLICT_MESSAGE_ENDING}`;
export const ALERTS_QH_CONFLICT_MESSAGE = `Quiet hours ${ALERTS_CONFLICT_MESSAGE_ENDING}`;
export const ALERTS_STO_CONFLICT_MESSAGE = `Send Time Optimization ${ALERTS_CONFLICT_MESSAGE_ENDING}`;

/**
 * Default recent alerts window duration
 */
export const RECENT_ALERTS_DURATION_WINDOW: TimeDuration = 'PT24H';

/**
 * lowercased days of the week names
 */
export const DAYS_OF_THE_WEEK = Object.values(Day);

/**
 * Recurrence schedules mapper
 * @see https://github.com/Iterable/Iterable/pull/14710#issue-1391134540
 */
export const RECURRENCE_SCHEDULE_MAPPER = {
  [RecurrenceScheduleType.DoNotRepeat]: null,
  [RecurrenceScheduleType.Daily]: {
    period: 'P1D',
    adjustments: [],
  },
  [RecurrenceScheduleType.Weekly]: {
    period: 'P7D',
    adjustments: [],
  },
  [RecurrenceScheduleType.BiWeekly]: {
    period: 'P14D',
    adjustments: [],
  },
  [RecurrenceScheduleType.ThirtyDays]: {
    period: 'P30D',
    adjustments: [],
  },
  [RecurrenceScheduleType.FirstOfTheMonth]: {
    period: 'P1M',
    adjustments: [
      {
        type: 'DayOfMonth',
        day: 1,
      },
    ],
  },
  [RecurrenceScheduleType.FifteenTheOfTheMonth]: {
    period: 'P1M',
    adjustments: [
      {
        type: 'DayOfMonth',
        day: 15,
      },
    ],
  },
  [RecurrenceScheduleType.LastOfTheMonth]: {
    period: 'P1M',
    adjustments: [
      {
        type: 'DayOfMonth',
        day: 31,
      },
    ],
  },
  [RecurrenceScheduleType.Custom]: {
    /**
     * `RecurrenceScheduleType.Custom` `template` period
     */
    period: _template('P<%= customFrequency %>D'),
    adjustments: [],
  },
};

/**
 * Used as default message when we cannot determine what cause an error with network calls
 */
export const DEFAULT_RESPONSE_ERROR_MESSAGE = 'Something went wrong';

/**
 * Default campaign metadata icon size
 */
export const CAMPAIGN_METADATA_ICON_SIZE = { height: 16, width: 16 };

/**
 * Campaign state badge variant mapper
 */
export const CAMPAIGN_STATE_BADGE_VARIANT_MAPPER = {
  [CampaignState.Draft]: BADGE_VARIANTS.DRAFT,
  [CampaignState.Ready]: BADGE_VARIANTS.READY,
  [CampaignState.Aborted]: BADGE_VARIANTS.ABORTED,
  [CampaignState.Archived]: BADGE_VARIANTS.FINISHED,
  [CampaignState.Finished]: BADGE_VARIANTS.FINISHED,
  [CampaignState.Recurring]: BADGE_VARIANTS.RECURRING,
  [CampaignState.Running]: BADGE_VARIANTS.RUNNING,
  [CampaignState.Recalling]: BADGE_VARIANTS.RECALL,
  [CampaignState.Recalled]: BADGE_VARIANTS.RECALL,
  [CampaignState.Scheduled]: BADGE_VARIANTS.SCHEDULED,
} as const;

/**
 * Template approval status badge mapper
 */
export const TEMPLATE_APPROVAL_STATUS_BADGE_MAP = {
  [MetaApprovalStatusDefinition.NotSubmitted]: <DraftBadge>Draft</DraftBadge>,
  [MetaApprovalStatusDefinition.Pending]: <ScheduledBadge>In review</ScheduledBadge>,
  [MetaApprovalStatusDefinition.Rejected]: <AbortedBadge>Rejected</AbortedBadge>,
  [MetaApprovalStatusDefinition.Approved]: <RunningBadge>Approved</RunningBadge>,
  [MetaApprovalStatusDefinition.InAppeal]: (
    <TopTooltip
      tooltipContent={
        'An appeal for this template was submitted in Meta Business Manager and is being reviewed. A decision should be made within 24 hours.'
      }
    >
      <InAppealBadge>In appeal</InAppealBadge>
    </TopTooltip>
  ),
  [MetaApprovalStatusDefinition.PendingDeletion]: (
    <TopTooltip
      tooltipContent={
        'The template was deleted in Meta Business Manager and is scheduled for removal. If it was previously sent, delivery will be attempted for up to 30 days before it becomes unavailable.'
      }
    >
      <AbortedBadge>Pending deletion</AbortedBadge>
    </TopTooltip>
  ),
  [MetaApprovalStatusDefinition.Deleted]: <AbortedBadge>Deleted</AbortedBadge>,
  [MetaApprovalStatusDefinition.Reinstated]: (
    <TopTooltip
      tooltipContent={
        'The template was previously disabled by Meta but has been restored and can be used again.'
      }
    >
      <RunningBadge>Reinstated</RunningBadge>
    </TopTooltip>
  ),
  [MetaApprovalStatusDefinition.Flagged]: <AbortedBadge>Flagged</AbortedBadge>,
  [MetaApprovalStatusDefinition.FirstPaused]: <AbortedBadge>Paused</AbortedBadge>,
  [MetaApprovalStatusDefinition.SecondPaused]: <AbortedBadge>Paused</AbortedBadge>,
  [MetaApprovalStatusDefinition.Disabled]: <AbortedBadge>Disabled</AbortedBadge>,
  [MetaApprovalStatusDefinition.Unknown]: <UnknownBadge>Unknown</UnknownBadge>,
};

export const QUALITY_RATING_MAP = {
  [MetaQualityRating.High]: (
    <TopTooltip tooltipContent="High quality">
      <RunningBadge>
        Approved <QualityRatingHigh />
      </RunningBadge>
    </TopTooltip>
  ),
  [MetaQualityRating.Medium]: (
    <TopTooltip tooltipContent="Medium quality">
      <ScheduledBadge>
        Approved <QualityRatingMedium />
      </ScheduledBadge>
    </TopTooltip>
  ),
  [MetaQualityRating.Low]: (
    <TopTooltip tooltipContent="Low quality, at risk of pause">
      <ScheduledBadge>
        Approved <QualityRatingLow />
      </ScheduledBadge>
    </TopTooltip>
  ),
  [MetaQualityRating.Unknown]: null,
};

/**
 * Quality rating badge mapper
 */

export const approvalStatusQualityMapper = (
  approvalStatus: MetaApprovalStatusDefinition,
  qualityRating?: Nullable<MetaQualityRating>,
) => {
  if (approvalStatus === MetaApprovalStatusDefinition.Approved && !!qualityRating) {
    return QUALITY_RATING_MAP[qualityRating];
  }
  return TEMPLATE_APPROVAL_STATUS_BADGE_MAP[approvalStatus];
};

/**
 * A category of locales
 */
export enum LocaleCategory {
  Active = 'Active',
  Available = 'Available',
  NotAvailableForWhatsApp = 'NotAvailableForWhatsApp',
}

/**
 * Locale category display label texts
 */
export const LOCALE_CATEGORY_LABELS = {
  [LocaleCategory.Active]: 'Active',
  [LocaleCategory.Available]: 'Available',
  [LocaleCategory.NotAvailableForWhatsApp]: 'Not available for WhatsApp',
} as const;
